// src/features/organisation/hooks/useCompetences.ts
import { useState, useEffect } from 'react';
import { apiClient } from '../../../services/api/client';
import { Competence, CompetenceCreateDto } from '../../../types/organisation/competences.types';

export const useCompetences = () => {
  const [competences, setCompetences] = useState<Competence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchCompetences = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/organisation/competences');
      // Calculer le statut pour chaque compétence
      const competencesWithStatus = (data || []).map((comp: Record<string, unknown>) => {
        let status = 'Valide';
        if (comp.expiryDate) {
          const expiryDate = new Date(comp.expiryDate);
          const now = new Date();
          const diffTime = expiryDate.getTime() - now.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          
          if (diffDays < 0) {
            status = 'Expiré';
          } else if (diffDays <= 30) {
            status = 'Expire bientôt';
          }
        }
        return { ...comp, status };
      });
      
      setCompetences(competencesWithStatus);
      setError(null);
    } catch (err) {
      setError(err as Error);
      setCompetences([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompetences();
  }, []);

  const createCompetence = async (data: CompetenceCreateDto): Promise<Competence> => {
    const newCompetence = await apiClient.post('/organisation/competences', data);
    await fetchCompetences(); // Rafraîchir la liste
    return newCompetence;
  };

  const updateCompetence = async (id: number, data: CompetenceCreateDto): Promise<Competence> => {
    const updatedCompetence = await apiClient.put(`/organisation/competences/${id}`, data);
    await fetchCompetences(); // Rafraîchir la liste
    return updatedCompetence;
  };

  const deleteCompetence = async (id: number): Promise<void> => {
    await apiClient.delete(`/organisation/competences/${id}`);

    await fetchCompetences(); // Rafraîchir la liste
  };

  return { 
    competences, 
    loading, 
    error, 
    refresh: fetchCompetences,
    createCompetence,
    updateCompetence,
    deleteCompetence
  };
};
